import {
  addDays,
  addMonths,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { Button } from './button';
import { Popover, PopoverContent, PopoverTrigger } from './overlays';

/**
 * A picked date. Date-only values are UTC midnight (`hasTime: false`) so they never shift across
 * time zones; values with a time are real instants shown in local time.
 */
export interface DateValue {
  ms: number;
  hasTime: boolean;
}

export function dateOnlyMs(year: number, monthIndex: number, day: number): number {
  return Date.UTC(year, monthIndex, day);
}

/** The local Date to display for a value (calendar day for date-only, local instant otherwise). */
export function toDisplayDate(value: DateValue): Date {
  if (value.hasTime) return new Date(value.ms);
  const d = new Date(value.ms);
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

export function todayDateOnly(offsetDays = 0): number {
  const now = new Date();
  return dateOnlyMs(now.getFullYear(), now.getMonth(), now.getDate() + offsetDays);
}

export function formatDateValue(
  value: DateValue | null | undefined,
  opts: { relative?: boolean } = {},
): string {
  if (!value) return '';
  const date = toDisplayDate(value);
  const now = new Date();
  let label: string;
  const diffDays = Math.round(
    (new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime() -
      new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()) /
      86_400_000,
  );
  if (opts.relative !== false && diffDays === 0) label = 'Today';
  else if (opts.relative !== false && diffDays === 1) label = 'Tomorrow';
  else if (opts.relative !== false && diffDays === -1) label = 'Yesterday';
  else label = format(date, date.getFullYear() === now.getFullYear() ? 'MMM d' : 'MMM d, yyyy');
  return value.hasTime ? `${label}, ${format(date, 'h:mm a')}` : label;
}

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

/** Month grid. Controlled by a local Date; the parent converts to its own value type. */
export function Calendar({
  selected,
  onSelect,
  rangeStart,
  rangeEnd,
}: {
  selected: Date | null;
  onSelect: (day: Date) => void;
  rangeStart?: Date | null;
  rangeEnd?: Date | null;
}) {
  const [month, setMonth] = useState(() => startOfMonth(selected ?? new Date()));
  const first = startOfWeek(startOfMonth(month));
  const last = endOfWeek(endOfMonth(month));
  const days: Date[] = [];
  for (let d = first; d <= last; d = addDays(d, 1)) days.push(d);
  const today = new Date();
  const inRange = (d: Date) => rangeStart && rangeEnd && d >= rangeStart && d <= rangeEnd;

  return (
    <div className="w-60 select-none">
      <div className="mb-1 flex items-center justify-between">
        <span className="px-1 text-sm font-semibold">{format(month, 'MMMM yyyy')}</span>
        <span className="flex">
          <Button
            size="icon-sm"
            variant="ghost"
            aria-label="Previous month"
            onClick={() => setMonth(addMonths(month, -1))}
          >
            <ChevronLeft />
          </Button>
          <Button
            size="icon-sm"
            variant="ghost"
            aria-label="Next month"
            onClick={() => setMonth(addMonths(month, 1))}
          >
            <ChevronRight />
          </Button>
        </span>
      </div>
      <div className="grid grid-cols-7 text-center text-2xs font-medium text-fg-subtle">
        {WEEKDAYS.map((d) => (
          <span key={d} className="py-1">
            {d}
          </span>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days.map((d) => {
          const isSelected = selected ? isSameDay(d, selected) : false;
          return (
            <button
              key={d.toISOString()}
              type="button"
              aria-label={format(d, 'EEEE, MMMM d, yyyy')}
              aria-pressed={isSelected}
              onClick={() => onSelect(d)}
              className={cn(
                'm-px flex h-8 items-center justify-center rounded-md text-sm tabular-nums hover:bg-hover',
                !isSameMonth(d, month) && 'text-fg-subtle',
                inRange(d) && !isSelected && 'bg-accent-soft',
                isSameDay(d, today) && !isSelected && 'font-semibold text-accent-text',
                isSelected && 'bg-accent font-semibold text-accent-fg hover:bg-accent-hover',
              )}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function timeString(value: DateValue): string {
  return format(new Date(value.ms), 'HH:mm');
}

/** Calendar with quick picks, optional time and clear. Used standalone or inside DatePicker. */
export function DatePanel({
  value,
  onChange,
  withTime = true,
  quickPicks = true,
}: {
  value: DateValue | null;
  onChange: (value: DateValue | null) => void;
  withTime?: boolean;
  /** Show the Today / Tomorrow / Next week shortcuts. */
  quickPicks?: boolean;
}) {
  const selected = value ? toDisplayDate(value) : null;

  const pickDay = (day: Date) => {
    if (value?.hasTime) {
      const prev = new Date(value.ms);
      const next = new Date(
        day.getFullYear(),
        day.getMonth(),
        day.getDate(),
        prev.getHours(),
        prev.getMinutes(),
      );
      onChange({ ms: next.getTime(), hasTime: true });
    } else {
      onChange({ ms: dateOnlyMs(day.getFullYear(), day.getMonth(), day.getDate()), hasTime: false });
    }
  };

  const setTime = (time: string) => {
    const base = selected ?? new Date();
    if (!time) {
      onChange({ ms: dateOnlyMs(base.getFullYear(), base.getMonth(), base.getDate()), hasTime: false });
      return;
    }
    const [h, m] = time.split(':').map(Number);
    onChange({
      ms: new Date(base.getFullYear(), base.getMonth(), base.getDate(), h ?? 0, m ?? 0).getTime(),
      hasTime: true,
    });
  };

  const quick: { label: string; offset: number }[] = [
    { label: 'Today', offset: 0 },
    { label: 'Tomorrow', offset: 1 },
    { label: 'Next week', offset: 7 },
  ];

  return (
    <div className="flex flex-col gap-2 p-1">
      {quickPicks ? (
        <div className="flex gap-1">
          {quick.map((q) => (
            <Button
              key={q.label}
              size="xs"
              variant="secondary"
              onClick={() => onChange({ ms: todayDateOnly(q.offset), hasTime: false })}
            >
              {q.label}
            </Button>
          ))}
        </div>
      ) : null}
      <Calendar
        key={selected ? `${selected.getFullYear()}-${selected.getMonth()}` : 'none'}
        selected={selected}
        onSelect={pickDay}
      />
      <div className="flex items-center justify-between gap-2 border-t border-border pt-2">
        {withTime ? (
          <label className="flex items-center gap-1.5 text-xs text-fg-muted">
            Time
            <input
              type="time"
              aria-label="Time"
              value={value?.hasTime ? timeString(value) : ''}
              onChange={(e) => setTime(e.target.value)}
              className="h-7 rounded-md border border-border-strong bg-surface px-1.5 text-sm text-fg"
            />
          </label>
        ) : (
          <span />
        )}
        <Button size="xs" variant="ghost" onClick={() => onChange(null)} disabled={!value}>
          <X /> Clear
        </Button>
      </div>
    </div>
  );
}

export function DatePicker({
  value,
  onChange,
  placeholder = 'Set date',
  withTime,
  children,
  className,
}: {
  value: DateValue | null;
  onChange: (value: DateValue | null) => void;
  placeholder?: string;
  withTime?: boolean;
  /** Custom trigger. Defaults to a button showing the formatted date. */
  children?: ReactNode;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {children ?? (
          <Button
            variant="outline"
            className={cn('justify-start font-normal', !value && 'text-fg-subtle', className)}
          >
            <CalendarIcon />
            {value ? formatDateValue(value) : placeholder}
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-auto">
        <DatePanel value={value} onChange={onChange} withTime={withTime} />
      </PopoverContent>
    </Popover>
  );
}
